import { useCallback, useMemo } from "react";
import { ROLE_LABEL } from "../../lib/i18n/roles";
import { useTeams } from "../../lib/queries/teams";
import { federationLabel, useFederations } from "../../lib/queries/federations";
import type { AudienceTargets } from "./AudienceFields";

/**
 * Reads targets back in one line („Vedoucí · Tým X"), or "" when nothing
 * narrows them; the caller words what everybody means for its content.
 */
export function useAudienceLabel(): (t: AudienceTargets) => string {
  const { data: teams = [], isLoading: teamsLoading } = useTeams();
  const { data: federations = [], isLoading: fedsLoading } = useFederations();
  const teamName = useMemo(() => new Map(teams.map((t) => [t.id, t.name])), [teams]);
  const fedName = useMemo(
    () => new Map(federations.map((f) => [f.id, federationLabel(f)])),
    [federations],
  );
  return useCallback(
    (t: AudienceTargets): string => {
      const roles = t.roles.map((r) => ROLE_LABEL[r]).join(", ");
      const scopes = [
        // An id missing from a list still loading is unknown, not deleted.
        ...t.team_ids.map((id) => teamName.get(id) ?? (teamsLoading ? "…" : "smazaný tým")),
        ...t.federation_ids.map(
          (id) => fedName.get(id) ?? (fedsLoading ? "…" : "smazaná federace"),
        ),
      ].join(", ");
      if (roles && scopes) return `${roles} · ${scopes}`;
      return roles || scopes;
    },
    [teamName, fedName, teamsLoading, fedsLoading],
  );
}
